import React, { useState, useEffect,useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Box,
  Chip,
  InputAdornment,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
// import DeleteIcon from "@mui/icons-material/Delete";
import scriptRunnerV2Service from "../../../services/scriptRunnerV2.service";
import ConfirmationDialog from "../../../components/confirmationdialog";
// import useScriptPermissions from "../hooks/useScriptPermissions";
import DownloadSelectionDialog from "./DownloadSelectionDialog";

const ExecutionTable = () => {
  // const { canWrite } = useScriptPermissions();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const order = "desc";
  const orderBy = "createdAt";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // interns scriptrunner - State for deletion confirmation
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    executionId: null,
  });

  // interns scriptrunner - State for selective download dialog
  const [downloadDialog, setDownloadDialog] = useState({
    open: false,
    executionId: null,
  });

  const fetchExecutions = useCallback(async () => {
  try {
    setLoading(true);
    const data = await scriptRunnerV2Service.getExecutions({
      search,
      sortBy: orderBy,
      sortOrder: order,
    });
    setExecutions(data);
  } catch (err) {
    console.error("Failed to fetch executions", err);
  } finally {
    setLoading(false);
  }
}, [search, order, orderBy]);

  // const handleDeleteClick = (e, executionId) => {
  //   e.currentTarget.blur();
  //   setConfirmDelete({ open: true, executionId });
  // }; }

  const handleDownloadClick = (e, executionId) => {
    e.currentTarget.blur();
    setDownloadDialog({ open: true, executionId });
  };

  const handleConfirmDelete = async () => {
    try {
      await scriptRunnerV2Service.deleteExecution(confirmDelete.executionId);
      await fetchExecutions();
    } catch (error) {
      console.error("interns scriptrunner - Failed to delete execution", error);
      alert("Failed to delete execution history. Please try again.");
    } finally {
      setConfirmDelete({ open: false, executionId: null });
    }
  };

  useEffect(() => {
  const timer = setTimeout(() => {
    fetchExecutions();
  }, 500);

  return () => clearTimeout(timer);
}, [fetchExecutions]);

  const totalPages = Math.ceil(executions.length / pageSize);

  const paginatedExecutions = executions.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const handleRefresh = async () => {
    if (loading) return;
    await fetchExecutions();
  };

  // const handleRequestSort = (property) => {
  //   const isAsc = orderBy === property && order === "asc";
  //   setOrder(isAsc ? "desc" : "asc");
  //   setOrderBy(property);
  // };

  const getStatusColor = (status) => {
    switch (status) {
      case "running":
        return "primary";
      case "paused":
        return "warning";
      case "completed":
        return "success";
      case "failed":
      case "terminated":
        return "error";
      default:
        return "default";
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return "-";
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  const buttonStyle = {
    minWidth: 80,
    borderColor: "#1d9f9f",
    color: "#1d9f9f",
    fontWeight: 600,
    textTransform: "none",
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* 🔥 HEADER */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Execution History
        </Typography>

        {/* CONTROLS */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            justifyContent: "flex-end",
            flex: 1,
          }}
        >
          {/* Refresh */}
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading}
            sx={buttonStyle}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>

          {/* Page Size */}
          <TextField
            select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            sx={{ width: 100 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </TextField>

          {/* Prev */}
          <Button
            variant="outlined"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            sx={buttonStyle}
          >
            Prev
          </Button>

          {/* Page */}
          <Typography sx={{ fontWeight: 500 }}>
            {page} / {totalPages || 1}
          </Typography>

          {/* Next */}
          <Button
            variant="outlined"
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages || totalPages === 0}
            sx={buttonStyle}
          >
            Next
          </Button>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search by name, status..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 220,
              flexGrow: 1,
              maxWidth: 300,
            }}
          />
        </Box>
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>Script Name</TableCell>
              <TableCell>Execution ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Pod Name</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Triggered By</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedExecutions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No executions found
                </TableCell>
              </TableRow>
            ) : (
              paginatedExecutions.map((exec) => (
                <TableRow key={exec._id}>
                  <TableCell>
                    {exec.scriptName}
                    <Chip label={exec.version} size="small" sx={{ ml: 1 }} />
                  </TableCell>
                  <TableCell>{exec._id}</TableCell>
                  <TableCell>
                    <Chip
                      label={exec.status}
                      color={getStatusColor(exec.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(exec.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>{exec.podName || "-"}</TableCell>
                  <TableCell>
                    {exec.endTime
                      ? new Date(exec.endTime).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>{formatDuration(exec.duration)}</TableCell>
                  <TableCell>{exec.executedBy}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Download">
                      <span>
                        <IconButton
                          onClick={(e) => handleDownloadClick(e, exec._id)}
                          disabled={exec.status === "running"}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* {canWrite && (
                      <Tooltip title="Delete History">
                        <IconButton
                          onClick={(e) => handleDeleteClick(e, exec._id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )} */}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Deletion Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDelete.open}
        handleClose={() => setConfirmDelete({ open: false, executionId: null })}
        handleConfirm={handleConfirmDelete}
        title="Confirm Delete Execution"
        message="Are you sure you want to delete this execution record from history? This action cannot be undone."
      />

      {/* Download Selection Dialog */}
      <DownloadSelectionDialog
        open={downloadDialog.open}
        handleClose={() =>
          setDownloadDialog({ open: false, executionId: null })
        }
        executionId={downloadDialog.executionId}
      />
    </Box>
  );
};

export default ExecutionTable;
